import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";

import { CoordinatesAlias } from "constant";
import { LocalStorageProvider } from "services/LocalStorageProvider";

import DropdownMenu, {
  DropDownMenuOptionInterface
} from "components/DropdownMenu/DropdownMenu";

import styles from "scenes/MapPage/_MapPage/map-page.module.scss";

interface ContextMenuInterface {
  position: any;
  options?: DropDownMenuOptionInterface[];
  visible: boolean;
  customMenu?: any;
}

const MapContext = createContext(null);

export const MapContextProvider = ({ children }) => {
  const mapRef = useRef(null);

  const [contextMenu, setContextMenu] = useState<ContextMenuInterface>({
    position: null,
    options: null,
    visible: false,
    customMenu: null
  });

  const [pointCoordinates, setPointCoordinates] = useState(null);

  const [coordinateSystem, setCoordinateSystem] = useState<CoordinatesAlias>(
    LocalStorageProvider.coordinates.getCurrentCoordinatesSystem() ||
      CoordinatesAlias.DD
  );

  const updateCoordinatesSystem = useCallback((сoordinatesSystem) => {
    setCoordinateSystem(сoordinatesSystem);
    LocalStorageProvider.coordinates.setCurrentCoordinatesSystem(
      сoordinatesSystem
    );
  }, []);

  const openContextMenu = ({ position, options, customMenu }) => {
    setContextMenu((prev) => ({
      ...prev,
      position: position ?? prev.position,
      options: options?.length > 0 ? options : prev?.options,
      visible: true,
      customMenu
    }));
  };

  const closeContextMenu = () => {
    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  const onDropdownMenuOptionClick = useCallback((option) => {
    setContextMenu({
      position: null,
      options: null,
      visible: false
    });
    option?.action && option.action(option);
  }, []);

  const ContextMenu = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isOverflowHidden, setIsOverflowHidden] = useState(true);

    useEffect(() => {
      if (contextMenu.visible || contextMenu.visible) setIsOverflowHidden(true);
      setTimeout(() => {
        setIsOverflowHidden(false);
        setIsVisible(contextMenu.visible || contextMenu.visible);
      }, 301);
    }, []);

    if (!contextMenu.visible) return null;

    return (
      <div
        className={styles["context-menu"]}
        style={{
          top: contextMenu.position?.y || contextMenu.position?.y,
          left: contextMenu.position?.x || contextMenu.position?.x,
          overflow: isOverflowHidden ? "hidden" : "unset"
        }}
      >
        {(contextMenu.options?.length > 0 ||
          contextMenu.customMenu !== null) && (
          <DropdownMenu
            options={contextMenu.options}
            isActive={isVisible}
            onClick={onDropdownMenuOptionClick}
            customMenu={contextMenu.customMenu}
          />
        )}
      </div>
    );
  };

  return (
    <MapContext.Provider
      value={{
        contextMenu,
        openContextMenu,
        closeContextMenu,
        pointCoordinates,
        setPointCoordinates,
        mapRef,
        coordinateSystem,
        updateCoordinatesSystem
      }}
    >
      {children}
      <ContextMenu />
    </MapContext.Provider>
  );
};

export const useMapContext = () => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error("useMapContext must be used within a MapContextProvider");
  }
  return context;
};
